import React, { useState, useEffect, useCallback } from "react";
import { Loader2, AlertTriangle, Eraser } from "lucide-react";
import { useChartAgentSocket } from "./hooks/useChartAgentSocket";
import { useConversation } from "./hooks/useConversation";
import { UiModeProvider, useUiMode } from "./hooks/useUiMode";
import { refreshAuth } from "./services/chartAgentSocket";
import ConversationList from "./components/ConversationList";
import ChatPane from "./components/ChatPane";
import Composer from "./components/Composer";
import { tenantApi } from "../../services/api";

function newConversationId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "c-" + Date.now() + "-" + Math.floor(Math.random() * 1000000);
}

// Derive which Socket.io namespace to connect to from the engine selection.
//   "ai"               → /ai          (legacy)
//   "ai-assistant"     → /ai-assistant (Sprint 7 — the rule-based brain)
//   "ai-assistant-llm" → /ai-assistant (Phase 2 — same namespace, LLM-driven brain)
//   everything else    → /ai2
function modeToAiVersion(mode) {
  if (mode === "ai-assistant" || mode === "ai-assistant-llm") return "ai-assistant";
  return mode === "ai" ? "ai" : "ai2";
}

// Compute the provider value sent in the socket payload.
//   "ai"               → undefined  (legacy /ai namespace doesn't use this field)
//   "ai2"              → the model dropdown value ("openai" or "anthropic")
//   "ai2-local"        → "local"    (backend ignores the model dropdown for local)
//   "ai-assistant"     → undefined  (backend picks per OPENAI_USE_RESPONSES_API; no UI model)
//   "ai-assistant-llm" → "llm-agent" (Phase 2 — dispatcher routes to runAgentLoopLlmAgent)
function resolveProvider(mode, model) {
  if (mode === "ai2-local")        return "local";
  if (mode === "ai2")              return model;
  if (mode === "ai-assistant-llm") return "llm-agent";
  return undefined;
}

const PANE_TITLES = {
  "ai-assistant":     "AI Assistant",
  "ai-assistant-llm": "AI Assistant (LLM-driven)",
  "ai":               "AI",
  "ai2":              "AI2",
  "ai2-local":        "AI2 Local",
};

export default function ChartAgentPage() {
  return (
    <UiModeProvider>
      <ChartAgentPageInner />
    </UiModeProvider>
  );
}

function ChartAgentPageInner() {
  const { uiMode } = useUiMode();
  // Engine selector — which socket namespace / server path to use.
  const [mode, setMode] = useState(() => localStorage.getItem("ai_mode") || "ai2");
  // Model selector — which LLM to use within the AI2 engine.
  const [provider, setProvider] = useState(() => localStorage.getItem("ai_provider") || "openai");
  // Sidebar collapse state — persisted across reloads. Defaults to expanded.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("ai_sidebar_collapsed") === "true",
  );

  // AI Assistant (Sprint 7) dev controls. env feeds the socket handshake (B-DEV-1) and
  // confidentialAccess simulates the prod JWT claim (B-CONF-3). Both ride the handshake, so
  // changing them refreshes the socket auth. Persisted in localStorage. Only shown for the
  // ai-assistant engine in dev uiMode.
  const [env, setEnv] = useState(() => localStorage.getItem("selected_env") || "dev");
  const [confidentialAccess, setConfidentialAccess] = useState(
    () => localStorage.getItem("ai_confidential_access") === "true",
  );
  // Both AI Assistant modes (rule-based + LLM-driven) ride the same /ai-assistant
  // namespace and need env + confidentialAccess on the socket handshake.
  const showAssistantControls = (mode === "ai-assistant" || mode === "ai-assistant-llm") && uiMode === "dev";

  const aiVersion = modeToAiVersion(mode);
  const effectiveProvider = resolveProvider(mode, provider);

  const { socket, connected, connectionError } = useChartAgentSocket(aiVersion);

  const [conversationId, setConversationId] = useState(() => newConversationId());

  const [tenants, setTenants] = useState([]);
  const [tenantSchema, setTenantSchema] = useState(() => localStorage.getItem("ai_active_tenant") || "");
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    tenantApi.getAll()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data.tenants || []);
        setTenants(list);
        setTenantsLoading(false);
        const stored = localStorage.getItem("ai_active_tenant") || "";
        const stillValid = stored && list.some((t) => (t.name || t.schema || t) === stored);
        if (!stillValid && list.length > 0) {
          const first = list[0].name || list[0].schema || list[0];
          setTenantSchema(first);
          localStorage.setItem("ai_active_tenant", first);
        } else if (stillValid) {
          setTenantSchema(stored);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load tenants:", err);
        setTenantsError(err.message || "Could not load tenants");
        setTenantsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const conv = useConversation({
    socket,
    conversationId,
    tenantSchema,
    provider: effectiveProvider,
  });

  const resetConversation = useCallback(() => {
    setConversationId(newConversationId());
  }, []);

  const handleTenantChange = (newTenant) => {
    setTenantSchema(newTenant);
    localStorage.setItem("ai_active_tenant", newTenant);
    resetConversation();
  };

  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    localStorage.setItem("ai_provider", newProvider);
    resetConversation();
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    localStorage.setItem("ai_mode", newMode);
    resetConversation();
  };

  // AI Assistant: env + tenant come from EnvTenantSelector. env rides the socket handshake
  // (read by the /ai-assistant env middleware), so changing it refreshes the auth and starts
  // a fresh conversation. tenant reuses the existing tenantSchema state + localStorage key.
  const handleEnvTenantChange = ({ env: nextEnv, tenant: nextTenant }) => {
    if (nextEnv && nextEnv !== env) {
      setEnv(nextEnv);
      localStorage.setItem("selected_env", nextEnv);
    }
    if (nextTenant && nextTenant !== tenantSchema) {
      setTenantSchema(nextTenant);
      localStorage.setItem("ai_active_tenant", nextTenant);
    }
    refreshAuth(); // push the new env into every active socket's handshake
    resetConversation();
  };

  // AI Assistant: confidentialAccess simulates the prod JWT claim in dev (B-CONF-3). It rides
  // the handshake, so flip + refresh + reset.
  const handleConfidentialChange = (next) => {
    setConfidentialAccess(next);
    localStorage.setItem("ai_confidential_access", next ? "true" : "false");
    refreshAuth();
    resetConversation();
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("ai_sidebar_collapsed", next ? "true" : "false");
      return next;
    });
  };

  const isInFlight = conv.state.status === "in_flight";

  if (connectionError) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">AI feature unavailable</h2>
          <p className="text-sm text-gray-600">{connectionError.message}</p>
          <p className="text-xs text-gray-400 mt-2">{connectionError.code}</p>
        </div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2} />
          <span className="text-sm">Connecting…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#FAFAFA]">
      <div
        className={
          "border-r border-gray-200 bg-white flex-shrink-0 transition-all duration-200 overflow-hidden " +
          (sidebarCollapsed ? "w-12" : "w-72")
        }
      >
        <ConversationList
          activeId={conversationId}
          onSelect={() => {}}
          onNew={resetConversation}
          tenants={tenants}
          tenantSchema={tenantSchema}
          onTenantChange={handleTenantChange}
          tenantsLoading={tenantsLoading}
          tenantsError={tenantsError}
          provider={provider}
          onProviderChange={handleProviderChange}
          mode={mode}
          onModeChange={handleModeChange}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={handleToggleSidebar}
        />
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        {!tenantSchema ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
            Select a tenant to begin
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 min-w-0 flex flex-col">
              <ChatPane
                title={PANE_TITLES[mode]}
                state={conv.state}
                connected={connected}
                onSelectExample={conv.sendQuery}
                onClarificationSelect={conv.selectClarificationOption}
                onFollowUpSelect={conv.sendQuery}
                showAssistantControls={showAssistantControls}
                env={env}
                tenant={tenantSchema}
                tenants={tenants}
                confidentialAccess={confidentialAccess}
                onEnvTenantChange={handleEnvTenantChange}
                onConfidentialChange={handleConfidentialChange}
                engine={mode}
                onEngineChange={handleModeChange}
              />
            </div>
            <div className="border-t border-gray-200 bg-white flex-shrink-0">
              <div className="px-6 py-4">
                <div className="max-w-3xl mx-auto flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <Composer
                      onSubmit={conv.sendQuery}
                      onCancel={conv.cancel}
                      isInFlight={isInFlight}
                      disabled={false}
                      awaitingClarification={false}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={resetConversation}
                    disabled={isInFlight}
                    className="flex-shrink-0 self-start mt-1 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Clear conversation"
                  >
                    <Eraser className="w-3.5 h-3.5" strokeWidth={2} />
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
